/**
 * Native wishlist — localStorage-backed, no app required.
 *
 * Storage: 'neonshrine-wishlist' = JSON array of product handles.
 * Hooks:
 *   [data-wishlist-toggle][data-product-handle]  heart buttons (cards + PDP)
 *   [data-wishlist-count]                        header badge(s)
 *   [data-wishlist-grid]                         wishlist page grid
 *   [data-wishlist-empty]                        wishlist page empty state
 *
 * Note: localStorage is per-browser/per-device — there is no account sync.
 * Upgradeable to customer metafields later without changing the hooks.
 */
(function () {
  'use strict';

  var KEY = 'neonshrine-wishlist';

  function read() {
    try {
      var list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) { /* private browsing / quota — wishlist is best-effort */ }
    updateBadges();
    syncButtons();
  }

  function toggle(handle) {
    var list = read();
    var i = list.indexOf(handle);
    if (i === -1) {
      list.push(handle);
      write(list);
      return true;
    }
    list.splice(i, 1);
    write(list);
    return false;
  }

  function updateBadges() {
    var count = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  function syncButtons() {
    var list = read();
    document.querySelectorAll('[data-wishlist-toggle][data-product-handle]').forEach(function (btn) {
      var saved = list.indexOf(btn.getAttribute('data-product-handle')) !== -1;
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
  }

  function toast(message, href, linkLabel) {
    document.dispatchEvent(new CustomEvent('toast:show', {
      detail: { message: message, href: href, linkLabel: linkLabel }
    }));
  }

  function formatMoney(cents) {
    try {
      return (cents / 100).toLocaleString(window.Shopify && Shopify.locale || undefined, {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'
      });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function imageUrl(src, width) {
    if (!src) return '';
    var url = src.indexOf('//') === 0 ? 'https:' + src : src;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ── Wishlist page grid ── */
  function renderGrid() {
    var grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    var emptyEl = document.querySelector('[data-wishlist-empty]');
    var handles = read();

    if (!handles.length) {
      grid.innerHTML = '';
      grid.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
      return;
    }

    grid.hidden = false;
    if (emptyEl) emptyEl.hidden = true;

    Promise.all(handles.map(function (handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js')
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      /* Prune products that no longer exist / were unpublished */
      var live = [];
      var kept = products.filter(function (p, i) {
        if (p) live.push(handles[i]);
        return !!p;
      });
      if (live.length !== handles.length) write(live);

      if (!kept.length) {
        grid.innerHTML = '';
        grid.hidden = true;
        if (emptyEl) emptyEl.hidden = false;
        return;
      }

      grid.innerHTML = kept.map(function (p) {
        var img = p.featured_image
          ? '<img class="product-tile__image" src="' + imageUrl(p.featured_image, 450) + '" alt="" loading="lazy">'
          : '<div class="product-tile__placeholder"></div>';
        var soldOut = p.available ? '' : '<div class="product-tile__badge"><span class="badge badge--yellow">Sold Out</span></div>';
        var compare = p.compare_at_price && p.compare_at_price > p.price
          ? '<span class="price price--compare">' + formatMoney(p.compare_at_price) + '</span>'
          : '';
        return (
          '<a href="' + p.url + '" class="card product-tile">' +
            '<div class="card__image">' + img + soldOut +
              '<button type="button" class="product-tile__wishlist" data-wishlist-toggle data-product-handle="' + p.handle + '" aria-pressed="true" aria-label="Remove from wishlist">' +
                '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>' +
              '</button>' +
            '</div>' +
            '<div class="card__body">' +
              '<h3 class="product-tile__title heading-sm">' + escapeHtml(p.title) + '</h3>' +
              '<div class="product-tile__price">' + compare + '<span class="price">' + formatMoney(p.price) + '</span></div>' +
            '</div>' +
          '</a>'
        );
      }).join('');
      syncButtons();
    });
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ── Events ── */

  /* Delegated toggle clicks — hearts sit inside card links, so block navigation */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var handle = btn.getAttribute('data-product-handle');
    if (!handle) return;

    var added = toggle(handle);
    if (added) {
      toast('Saved to wishlist', '/pages/wishlist', 'View');
    } else if (btn.closest('[data-wishlist-grid]')) {
      renderGrid();
    }
  });

  /* Cross-tab sync */
  window.addEventListener('storage', function (e) {
    if (e.key !== KEY) return;
    updateBadges();
    syncButtons();
    renderGrid();
  });

  function init() {
    updateBadges();
    syncButtons();
    renderGrid();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  /* Theme editor: re-sync hearts in re-rendered sections */
  document.addEventListener('shopify:section:load', function () {
    updateBadges();
    syncButtons();
  });
})();
